import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { BardText, DrivingSchoolHeaderBlock } from '../../@types/statamic'
import Container from '../../components/layout/Container'
import { PageInfo } from '@/src/@types/models'
import Bard from '@/src/components/bard/Bard'
import Button from '@/src/components/core/Button'
import ConditionalWrapper from '@/src/components/core/ConditionalWrapper'
import CustomLink from '@/src/components/elements/CustomLink'
import JsonLd from '@/src/components/elements/JsonLd'
import Picture from '@/src/components/elements/Picture'
import StarRating from '@/src/components/elements/StarRating'
import styles from '@/src/css/Masks.module.css'
import siteInfo from '@/src/lib/siteInfo'

export function getTitle({ title }: DrivingSchoolHeaderBlock) {
  return title
}

export function getDescription({ hero_content }: DrivingSchoolHeaderBlock) {
  const bardText = hero_content.find((bard) => bard.type === 'text') as BardText
  if (bardText) {
    const pTag = bardText.text.match(/.*?<p>(.*?)<\/p>/)
    if (pTag) {
      return pTag[1]
    }
  }
  return undefined
}

export default function Component({
  title,
  heading,
  hero_content,
  thumbnail,
  optional_video: video,
  images,
  link_link,
  link_text,
  google_rating,
  pageInfo,
}: DrivingSchoolHeaderBlock & { pageInfo: PageInfo }) {
  let videoType = 'video/mp4'

  if (video?.endsWith('.webm')) {
    videoType = 'video/webm'
  }
  // possibly more types in the future

  return (
    <>
      {pageInfo && google_rating && (
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'DrivingSchool',
            name: siteInfo.orgName,
            description: siteInfo.locationName(google_rating.place),
            address: {
              '@type': 'PostalAddress',
              streetAddress: google_rating.street,
              addressLocality: google_rating.place,
              postalCode: google_rating.zip,
            },
            aggregateRating:
              google_rating.rating && google_rating.rating > 0
                ? {
                    '@type': 'AggregateRating',
                    ratingValue: google_rating.rating,
                    ratingCount: google_rating.rating_count,
                    bestRating: 5,
                    worstRating: 1,
                  }
                : undefined,
            geo:
              google_rating.latitude && google_rating.longitude
                ? {
                    '@type': 'GeoCoordinates',
                    latitude: google_rating.latitude,
                    longitude: google_rating.longitude,
                  }
                : undefined,
            // priceRange: courses[0] ? `${courses[0].price} CHF` : undefined,
            // hasMap: `https://g.page/${gmbShortname}`,
            openingHours: ['Mo,Tu,We,Th,Fr 09:00-17:00', 'Sa 09:00-12:00'],
            telephone: google_rating.phone,
            logo: pageInfo.settings.logo?.permalink,
            url: pageInfo.fullUrl,
            image: pageInfo.socialImage
              ? [pageInfo.socialImage?.permalink]
              : undefined,
          }}
        />
      )}
      <div className="bg-linear-to-b from-gray-50 to-gray-100 pt-16 pb-12 xl:pt-27">
        <Container fullPageOnMobile>
          <div className="grid min-h-140 md:grid-cols-2 md:gap-16">
            <div className="@container order-2 flex w-full flex-col justify-center px-5 pt-8 md:order-1 md:px-0 md:py-16">
              <h1 className="font-bold text-balance md:text-lg">{title}</h1>
              <h2 className="mt-2 text-7xl font-black md:text-25xl">
                {heading}
              </h2>
              <Bard content={hero_content} className="mt-5 md:mt-6" fullWidth />
              <div className="mt-8 flex flex-col items-center space-y-4 @lg:flex-row @lg:space-y-0 @lg:space-x-8">
                {link_link?.url && link_text && (
                  <CustomLink href={link_link.url} passHref>
                    <Button as="a" className="w-full @lg:w-auto">
                      {link_text}
                    </Button>
                  </CustomLink>
                )}
                {google_rating &&
                  !!google_rating.rating &&
                  google_rating.rating > 0 && (
                    <ConditionalWrapper
                      when={google_rating.rating_url}
                      wrapper={(children, ratingUrl) => (
                        <Link
                          href={ratingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {children}
                        </Link>
                      )}
                    >
                      <div className="flex flex-col items-center @lg:items-start">
                        <StarRating rating={google_rating.rating} />
                        <p className="mt-0.5 text-sm">
                          {google_rating.rating_count} Bewertungen auf Google
                        </p>
                      </div>
                    </ConditionalWrapper>
                  )}
              </div>
            </div>
            {video && thumbnail && (
              <div className="order-1 aspect-3/2 w-full overflow-hidden md:order-2 md:aspect-auto md:min-h-140 md:w-210 md:rounded-2xl md:shadow-sm">
                <video
                  playsInline
                  autoPlay
                  muted
                  loop
                  preload="auto"
                  poster={thumbnail.permalink}
                  webkit-playsinline="true"
                  className={clsx([
                    'size-full object-cover object-center',
                    styles['mask'],
                    styles['mask-8'],
                  ])}
                >
                  <source src={video} type={videoType} />
                  Your browser does not support HTML video.
                </video>
              </div>
            )}
            {!(video && thumbnail) && images.length > 0 && (
              <div className="order-1 md:order-2">
                <ImageCarousel images={images} />
              </div>
            )}
          </div>
        </Container>
      </div>
    </>
  )
}

const ImageCarousel = ({
  images,
}: {
  images: DrivingSchoolHeaderBlock['images']
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
  })

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <div
      className="relative aspect-[3/2] overflow-hidden md:aspect-auto md:h-140 md:w-210 md:rounded-2xl md:shadow-sm"
      ref={emblaRef}
    >
      <ul className="flex h-full select-none">
        {images.map((image, index) => (
          <li key={index} className="relative min-w-0 flex-[0_0_100%]">
            <Picture
              {...image}
              fill
              sizes=""
              priority
              className={clsx('object-cover', styles['mask'], styles['mask-8'])}
            />
          </li>
        ))}
      </ul>
      <div className="absolute bottom-4 left-1/2 flex space-x-1.5 md:left-[calc((min(840px,50dvw-40px))/2)] md:-translate-x-1/2">
        {images.map((_, index) => (
          <div
            key={index}
            className={clsx('rounded-full transition-all', {
              'size-2 bg-white/60': selectedIndex !== index,
              'h-2 w-3 bg-white': selectedIndex === index,
            })}
          />
        ))}
      </div>
    </div>
  )
}
